import styled from 'styled-components'
import highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import ReactDOMServer from 'react-dom/server'

export interface DonutChartData {
  name: string
  y: number
}

interface DonutChartProps {
  title?: string
  xAxisLabel?: string
  yAxisLabel?: string
  data: DonutChartData[]
  colors?: string[]
  displayLegend?: boolean
  displaySubtitle?: boolean
  fitContainer?: boolean
  displayLabels?: boolean
  slicing?: boolean
  size?: number
  customTooltip?: (type: string, value: number) => JSX.Element
  labelAlign?: string
  pieSize?: string | number
  labelFormatter?: () => void
  labelItemWidth?: number | string
  legendTitle?: Highcharts.TitleOptions
  renderSubtitle?: (title: number) => void
  tooltipLabel?: string
}

export const DonutChart = ({
  title,
  xAxisLabel,
  yAxisLabel,
  data,
  colors,
  displayLegend = false,
  displaySubtitle = true,
  fitContainer = false,
  displayLabels = true,
  slicing = false,
  size,
  labelAlign = 'right',
  pieSize,
  labelFormatter,
  labelItemWidth = 60,
  legendTitle,
  renderSubtitle,
  customTooltip,
  tooltipLabel,
}: DonutChartProps) => {
  const total = data.reduce(
    (sum: number, item: { y: number }) => sum + item.y,
    0
  )

  const chartSubtitle = renderSubtitle
    ? renderSubtitle(total)
    : {
        useHTML: true,
        text: `<span style="font-size: 18px; color: black"><b>${total} Total</b></span>`,
        verticalAlign: 'bottom',
        align: 'center',
        style: {
          height: '24px',
        },
        y: 20,
      }

  const config = {
    title: {
      text: title,
      align: 'left',
    },
    subtitle: displaySubtitle ? chartSubtitle : null,
    yAxis: {
      title: {
        text: yAxisLabel,
      },
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: xAxisLabel,
      },
    },
    chart: {
      height: size || 230,
      width: size || null,
      ...(fitContainer && {
        margin: [24, 24, 24, 24],
        backgroundColor: null,
        plotBackgroundColor: null,
      }),
    },
    tooltip: {
      valueDecimals: 0,
      ...(customTooltip && {
        useHTML: true,
        formatter() {
          // eslint-disable-next-line
          const point = this as any
          const type = point.key as string
          const value = point.y as number

          return ReactDOMServer.renderToString(customTooltip(type, value))
        },
        backgroundColor: 'transparent',
      }),
    },
    plotOptions: {
      series: {
        borderWidth: 0,
        colorByPoint: true,
        type: 'pie',
        size: fitContainer ? '100%' : '140%',
        innerSize: '70%',
        dataLabels: {
          enabled: displayLabels,
          distance: -10,
          format: '<span style="color: black">{point.y}</span>',
          style: {
            fontSize: '0.9em',
            textOutline: 'none',
            whiteSpace: 'normal',
            textOverflow: 'none',
          },
        },
      },
      pie: {
        showInLegend: true,
        ...(slicing && {
          borderWidth: 2,
          borderColor: '#fff',
        }),
        size: pieSize,
      },
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: displayLegend,
      layout: 'vertical',
      align: labelAlign,
      verticalAlign: 'middle',
      itemWidth: labelItemWidth,
      itemStyle: {
        whiteSpace: 'normal',
        textOverflow: 'none',
      },
      x: 20,
      useHTML: true,
      ...(labelFormatter ? { labelFormatter } : {}),
      ...(legendTitle ? { title: legendTitle } : {}),
    },
    series: [
      {
        type: 'pie',
        name: tooltipLabel || '',
        data,
      },
    ],
    colors,
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 200,
          },
          chartOptions: {
            chart: {
              height: 240,
            },
            series: [
              {
                size: '110%',
              },
            ],
          },
        },
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            chart: {
              height: size || 240,
            },
            series: [
              {
                size: '120%',
              },
            ],
          },
        },
      ],
    },
  }
  return fitContainer ? (
    <HighchartsReact highcharts={highcharts} options={config} />
  ) : (
    <Container>
      <HighchartsReact highcharts={highcharts} options={config} />
    </Container>
  )
}

export default DonutChart

const Container = styled.div`
  border-radius: 25px;
  background-size: 90%;
  background-position-x: 35%;
  background-position-y: center;
  background-repeat: no-repeat;
  color: ${({ theme }) => theme.color.neutral.color11};

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    background-size: 250%;
    background-position-x: 5%;
    background-position-y: bottom;
  }
`
